/**
 * MerchPopup
 *
 * Mirrors PromoPopup but for new merch drops. Only fires AFTER the promo popup
 * check returns null, so the customer never gets two popups stacked on login.
 *
 * Customer can: "Maybe Later" (dismiss, mark seen) or "View in Shop" (mark seen
 * + navigate to /merch).
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth, api } from '../context/AuthContext';
import { X, ShoppingBag, Tag } from 'lucide-react';
import { Button } from './ui/button';

const SKIP_PATHS = ['/login', '/signup', '/forgot-password', '/reset-password'];

const MerchPopup = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [item, setItem] = useState(null);
  const [closing, setClosing] = useState(false);
  const [marking, setMarking] = useState(false);

  const fetchPopup = useCallback(async () => {
    if (!currentUser) return;
    if (SKIP_PATHS.some(p => location.pathname.startsWith(p))) return;
    if (currentUser.role === 'admin' || currentUser.role === 'barber') return;

    try {
      // 1. Check promo popup first — defer if a promo will show
      try {
        const promoRes = await api.get('/api/promotions/popup');
        if (promoRes.data?.promotion) {
          // A promo is queued — don't double-popup
          return;
        }
      } catch (_) {
        // proceed even if promo endpoint errors
      }
      // 2. Get the next unseen merch item
      const res = await api.get('/api/merch/popup');
      if (res.data?.item) {
        setItem(res.data.item);
      }
    } catch (e) {
      console.debug('[MerchPopup] fetch skipped', e?.response?.status);
    }
  }, [currentUser, location.pathname]);

  useEffect(() => { fetchPopup(); }, [fetchPopup]);

  const markSeen = useCallback(async () => {
    if (!item || marking) return;
    setMarking(true);
    try {
      await api.post(`/api/merch/${item.id}/seen`);
    } catch (e) {
      console.debug('[MerchPopup] mark seen failed', e?.response?.status);
    } finally {
      setMarking(false);
    }
  }, [item, marking]);

  const handleClose = async () => {
    setClosing(true);
    await markSeen();
    setItem(null);
    setClosing(false);
  };

  const handleView = async () => {
    setClosing(true);
    await markSeen();
    setItem(null);
    setClosing(false);
    navigate('/merch');
  };

  if (!item) return null;

  const price = Number(item.price ?? 0).toFixed(2);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-300"
      onClick={handleClose}
      data-testid="merch-popup-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md bg-gradient-to-br from-zinc-950 via-purple-950 to-zinc-950 border-2 border-lime-400/40 rounded-2xl shadow-2xl shadow-lime-500/20 overflow-hidden animate-in zoom-in-95 duration-300"
        data-testid="merch-popup"
      >
        <button
          onClick={handleClose}
          disabled={closing}
          className="absolute top-3 right-3 z-10 p-1.5 rounded-full bg-black/40 hover:bg-black/60 text-zinc-300 hover:text-white transition"
          aria-label="Close"
          data-testid="merch-popup-close-btn"
        >
          <X className="w-5 h-5" />
        </button>

        {item.image_url ? (
          <div className="relative aspect-[16/10] bg-zinc-900 flex items-center justify-center">
            <img
              src={item.image_url}
              alt={item.name}
              className="w-full h-full object-contain"
              data-testid="merch-popup-image"
            />
          </div>
        ) : (
          <div className="aspect-[16/10] bg-zinc-900 flex items-center justify-center">
            <ShoppingBag className="w-20 h-20 text-zinc-700" />
          </div>
        )}

        <div className="p-6 text-center">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 mb-3 rounded-full bg-lime-400/15 border border-lime-400/40 text-lime-300 text-xs font-semibold uppercase tracking-wider">
            <Tag className="w-3.5 h-3.5" /> New in shop
          </div>

          <h2 className="text-2xl font-bold text-white mb-2 leading-tight" data-testid="merch-popup-title">
            {item.name}
          </h2>

          <p className="text-3xl font-extrabold text-lime-400 mb-3 tracking-tight" data-testid="merch-popup-price">
            ${price}
          </p>

          {item.description && (
            <p className="text-zinc-300 text-sm mb-4 leading-relaxed">
              {item.description}
            </p>
          )}

          {item.sizes && item.sizes.length > 0 && (
            <p className="text-zinc-400 text-xs mb-4">
              Available: {item.sizes.join(', ')}
            </p>
          )}

          <div className="flex flex-col sm:flex-row gap-2 mt-4">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={closing}
              className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-white"
              data-testid="merch-popup-maybe-later-btn"
            >
              Maybe later
            </Button>
            <Button
              onClick={handleView}
              disabled={closing}
              className="flex-1 bg-lime-400 text-black hover:bg-lime-500 font-bold"
              data-testid="merch-popup-view-btn"
            >
              View in shop →
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MerchPopup;
